import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { getProjectContext, setCurrentProject, setCurrentSchedule, setCurrentOrganization, clearProjectContext } from '../api/projectContext';
import { Project, Schedule } from '../types/project';
// Re-export the project store and provider from the TSX version for compatibility
export { useProjectStore, ProjectContextProvider } from './ProjectContext.tsx';

export interface Organization {
  id: string;
  name: string;
}

export interface ProjectContextState {
  // Context data
  organization: Organization | null;
  project: Project | null;
  schedule: Schedule | null;
  roles: string[];
  permissions: string[];
  
  // Status
  isLoading: boolean;
  error: string | null;
  
  // Actions
  loadContext: () => Promise<void>;
  setProject: (projectId: string) => Promise<void>;
  setSchedule: (scheduleId: string) => Promise<void>;
  setOrganization: (orgId: string) => Promise<void>;
  clearContext: () => Promise<void>;
}

export const useProjectContext = create<ProjectContextState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        organization: null,
        project: null,
        schedule: null,
        roles: [],
        permissions: [],
        isLoading: false,
        error: null,
        
        // Load context from API
        loadContext: async () => {
          try {
            set({ isLoading: true, error: null });
            const context = await getProjectContext();
            
            set({
              organization: context.org && context.org.id ? context.org : null,
              project: context.project && context.project.id ? context.project : null,
              schedule: context.schedule && context.schedule.id ? context.schedule : null,
              roles: context.roles || [],
              permissions: context.permissions || [],
              isLoading: false
            });
          } catch (error) {
            console.error('Error loading project context:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to load context'
            });
          }
        },
        
        // Set current project
        setProject: async (projectId: string) => {
          try {
            set({ isLoading: true, error: null });
            await setCurrentProject(projectId);
            await get().loadContext();
          } catch (error) {
            console.error('Error setting project:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to set project'
            });
          }
        },
        
        // Set current schedule
        setSchedule: async (scheduleId: string) => {
          try {
            set({ isLoading: true, error: null });
            await setCurrentSchedule(scheduleId);
            await get().loadContext();
          } catch (error) {
            console.error('Error setting schedule:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to set schedule'
            });
          }
        },
        
        // Set current organization
        setOrganization: async (orgId: string) => {
          try {
            set({ isLoading: true, error: null });
            await setCurrentOrganization(orgId);
            await get().loadContext();
          } catch (error) {
            console.error('Error setting organization:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to set organization'
            });
          }
        },
        
        // Clear context
        clearContext: async () => {
          try {
            set({ isLoading: true, error: null });
            await clearProjectContext();
            set({
              organization: null,
              project: null,
              schedule: null,
              roles: [],
              permissions: [],
              isLoading: false
            });
          } catch (error) {
            console.error('Error clearing context:', error);
            set({
              isLoading: false,
              error: error instanceof Error ? error.message : 'Failed to clear context'
            });
          }
        }
      }),
      {
        name: 'project-context-storage',
        partialize: (state) => ({
          organization: state.organization,
          project: state.project,
          schedule: state.schedule,
          roles: state.roles,
          permissions: state.permissions
        })
      }
    )
  )
);

// Export useProjectContext as useProject for compatibility
export const useProject = useProjectContext; 